import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/marketing/Header";
import MarketingFooter from "@/components/marketing/Footer";
import QuoteForm from "@/components/marketing/QuoteForm";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight, Clock3, Globe2, ShieldCheck, Stethoscope, Scale, Briefcase } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/5452230/pexels-photo-5452230.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ABOUT_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBpbnRlcnByZXRlciUyMGhlYWx0aGNhcmUlMjBjb25zdWx0YXRpb24lMjBkb2N0b3IlMjBwYXRpZW50fGVufDB8fHx8MTc4ODQwMzM2NXww&ixlib=rb-4.1.0&q=85";

const INDUSTRY_CARDS = [
  { icon: <Stethoscope className="h-6 w-6" />, title: "Healthcare", body: "Patient intake, consent, discharge — clinical conversations handled with care." },
  { icon: <Scale className="h-6 w-6" />, title: "Legal", body: "Consultations, depositions and client meetings with precise, confidential interpretation." },
  { icon: <Briefcase className="h-6 w-6" />, title: "Business", body: "Negotiations, support desks and international teams, understood the first time." },
];

export default function Home() {
  const [content, setContent] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const home = content?.home || {};

  return (
    <div className="min-h-screen bg-white">
      <MarketingHeader />

      {/* Hero */}
      <section className="relative bg-[#0a2540] text-white overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a2540] via-[#0a2540]/85 to-[#0a2540]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-400" />
              24/7 on-demand interpretation
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              {home.hero_title || "Language Access, Driven by Care."}
            </h1>
            <p className="mt-6 text-white/75 text-lg leading-relaxed">
              {home.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact">
                <Button size="lg" data-testid="hero-quote-btn"
                  className="rounded-full bg-yellow-400 text-[#0a2540] hover:bg-yellow-300 font-semibold h-12 px-7">
                  Get a Quote <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" data-testid="hero-services-btn"
                  className="rounded-full border-white/40 text-white hover:bg-white/10 h-12 px-7">
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-3 gap-6 text-center">
          <Stat k="150+" v="Languages covered" />
          <Stat k="<30s" v="Average connect time" />
          <Stat k="24/7" v="Interpreter availability" />
        </div>
      </section>

      {/* Industries */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-medium text-[#0a2540]">
            {home.about_title || "Built for the conversations that matter most."}
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">{home.about_body}</p>
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {INDUSTRY_CARDS.map((c) => (
            <div key={c.title} className="border border-slate-200 rounded-2xl p-8 bg-white hover:shadow-lg transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600">{c.icon}</div>
              <h3 className="mt-5 font-display text-xl font-medium text-[#0a2540]">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About split */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <img src={ABOUT_IMG} alt="Healthcare consultation" className="rounded-2xl shadow-md object-cover w-full h-[380px]" />
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-[#0a2540]">How it works</h2>
            <div className="mt-8 space-y-6">
              <Step n="1" title="Tell us your languages" body="We set up your account with the languages and rates that fit your organization." />
              <Step n="2" title="Request an interpreter" body="Your staff picks a language and taps one button. We route the call to an online interpreter." />
              <Step n="3" title="Talk. We handle the rest" body="Crystal-clear voice in the browser. Every call metered to the second, itemized in monthly statements." />
            </div>
          </div>
        </div>
      </section>

      {/* News */}
      {posts.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-24">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-medium text-[#0a2540]">Latest from Interpreta</h2>
            <Link to="/news" className="text-teal-600 font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all">
              All news <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link key={p.slug} to={`/news/${p.slug}`}
                className="border border-slate-200 rounded-2xl p-7 bg-white hover:shadow-lg transition-shadow block">
                <div className="text-xs text-slate-400">{new Date(p.created_at).toLocaleDateString()}</div>
                <h3 className="mt-2 font-display text-lg font-medium text-[#0a2540] leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Quote CTA */}
      <section id="quote" className="bg-[#0a2540] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-medium">{home.cta_title || "Ready to break the language barrier?"}</h2>
            <p className="mt-4 text-white/70 leading-relaxed max-w-md">{home.cta_body}</p>
            <div className="mt-8 flex items-center gap-6 text-sm text-white/60">
              <span className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-teal-400" /> Under 30s connect</span>
              <span className="flex items-center gap-2"><Globe2 className="h-4 w-4 text-teal-400" /> 150+ languages</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-teal-400" /> Vetted interpreters</span>
            </div>
          </div>
          <div className="bg-white text-slate-900 rounded-2xl p-8 shadow-xl">
            <h3 className="font-display text-2xl font-medium text-[#0a2540] mb-6">Get a quote</h3>
            <QuoteForm />
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="font-display text-3xl sm:text-4xl font-medium">{k}</div>
      <div className="text-xs uppercase tracking-widest text-white/70 mt-1">{v}</div>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="h-9 w-9 rounded-full bg-[#0a2540] text-white flex items-center justify-center font-display font-medium shrink-0">{n}</div>
      <div>
        <div className="font-medium text-[#0a2540]">{title}</div>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
